import { useCallback, useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { MagnifyingGlass, Plus, CaretLeft, CaretRight, Brain, Eye, Spinner, CheckCircle } from "@phosphor-icons/react";
import { BloomButton } from "@/components/styleguide/ui/BloomButton";
import { BloomInput } from "@/components/styleguide/ui/BloomInput";
import { BloomBadge } from "@/components/styleguide/ui/BloomBadge";
import { cn } from "@/lib/utils";
import { api } from "../api";
import { WordEditor } from "./WordEditor";
import { WordPreview } from "./WordPreview";
import { QuizBankModal } from "./QuizBankModal";

export const STATUS_VARIANT = { approved: "success", draft: "neutral", review: "warning", rejected: "primary", archived: "neutral" };

const FILTERS = ["", "approved", "draft", "review", "rejected", "archived"];

export default function AdminWords() {
  const [data,          setData]          = useState(null);
  const [search,        setSearch]        = useState("");
  const [status,        setStatus]        = useState("");
  const [page,          setPage]          = useState(1);
  const [editor,        setEditor]        = useState(null); // {word} | {new:true} | {similarTo}
  const [preview,       setPreview]       = useState(null); // word being previewed
  const [quizModal,     setQuizModal]     = useState(null); // word whose quiz bank to view
  const [generatingId,  setGeneratingId]  = useState(null); // word_id currently being generated
  const [hasQuiz,       setHasQuiz]       = useState(false); // "Has Quiz" filter toggle
  // Set of word_ids that have a quiz bank — populated from API + updated locally
  const [bankIds,       setBankIds]       = useState(new Set());

  const load = useCallback(async () => {
    const d = await api(`/admin/words?search=${encodeURIComponent(search)}&status=${status}&page=${page}&limit=15&has_quiz=${hasQuiz}`);
    setData(d);

    // Seed bankIds from has_quiz_bank flag returned by the API (if the backend supports it)
    if (d?.items) {
      setBankIds((prev) => {
        const next = new Set(prev);
        d.items.forEach((w) => { if (w.has_quiz_bank) next.add(w.word_id); });
        return next;
      });
    }
  }, [search, status, page, hasQuiz]);

  useEffect(() => {
    const t = setTimeout(() => load().catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [load]);

  // Open preview on word row click (edit from within preview)
  const handleWordClick = (w) => setPreview(w);

  // Generate quiz bank for a single word directly from the row
  async function handleGenerateQuiz(w) {
    setGeneratingId(w.word_id);
    try {
      const data = await api("/admin/quiz-bank/generate", {
        method: "POST",
        body:   { words: w.word },
      });
      const result = data.results?.[0];
      if (result?.status === "ok") {
        setBankIds((prev) => new Set([...prev, w.word_id]));
        // Auto-open the quiz modal so admin can immediately review
        setQuizModal(w);
      } else {
        const detail = result?.detail ?? "Generation failed";
        alert(`Could not generate quiz for "${w.word}": ${detail}`);
      }
    } catch (err) {
      alert(err?.message ?? "Request failed");
    } finally {
      setGeneratingId(null);
    }
  }

  return (
    <div className="mx-auto max-w-5xl" data-testid="admin-words">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight text-foreground">Master Vocabulary</h1>
          <p className="mt-1 text-sm font-semibold text-muted-foreground">{data ? `${data.total} words` : "…"} · one library, one source of truth.</p>
        </div>
        <BloomButton size="sm" leftIcon={Plus} onClick={() => setEditor({ new: true })} data-testid="new-word-btn">New Word</BloomButton>
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="max-w-sm flex-1">
          <BloomInput icon={MagnifyingGlass} placeholder="Search words, synonyms, tags…" value={search} data-testid="word-search-input"
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((s) => (
            <button key={s || "all"} data-testid={`status-filter-${s || "all"}`} onClick={() => { setStatus(s); setPage(1); }}
              className={cn("rounded-full border-2 px-3 py-1.5 text-xs font-extrabold capitalize transition", status === s ? "border-primary bg-accent text-primary-strong" : "border-border text-muted-foreground hover:border-primary/40")}>
              {s || "All"}
            </button>
          ))}
          {/* Separator */}
          <span className="mx-1 self-center text-border">|</span>
          {/* Has Quiz toggle — independent of status filter */}
          <button
            data-testid="filter-has-quiz"
            onClick={() => { setHasQuiz((v) => !v); setPage(1); }}
            className={cn(
              "flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-xs font-extrabold transition",
              hasQuiz
                ? "border-primary bg-primary text-white"
                : "border-border text-muted-foreground hover:border-primary/40"
            )}
          >
            <Brain size={12} weight="bold" />
            Has Quiz
          </button>
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
        {data === null && <div className="h-64 animate-pulse bg-muted/50" />}
        {data?.items.map((w) => {
          const hasBank = bankIds.has(w.word_id);
          const isGenerating = generatingId === w.word_id;
          return (
            // Row is a <div> so we can nest interactive buttons inside it
            <div
              key={w.word_id}
              data-testid={`word-row-${w.normalized}`}
              className="flex w-full items-center gap-3 border-b border-border px-4 py-3 last:border-0 hover:bg-muted/40 transition"
            >
              {/* Clickable text area — opens preview */}
              <button
                onClick={() => handleWordClick(w)}
                className="min-w-0 flex-1 text-left"
              >
                <span className="block font-display text-base font-extrabold capitalize text-foreground">{w.word}</span>
                <span className="block truncate text-xs font-semibold text-muted-foreground">{w.meaning || "No meaning yet — needs content"}</span>
              </button>

              {/* Metadata (hidden on small screens) */}
              <span className="hidden text-xs font-bold text-muted-foreground sm:block">{w.word_type || "—"}</span>
              <span className="hidden text-xs font-bold text-muted-foreground md:block">{w.years?.length ? `Y${w.years.join(", Y")}` : "—"}</span>
              {w.gs_11plus && <BloomBadge variant="blue" className="hidden px-2 py-0.5 text-[10px] md:inline-flex">11+</BloomBadge>}
              <BloomBadge variant={STATUS_VARIANT[w.status]} className="px-2.5 py-0.5 text-xs capitalize">{w.status}</BloomBadge>

              {/* Quiz action buttons */}
              <div className="flex flex-shrink-0 items-center gap-1">
                {/* Generate quiz — always visible */}
                <button
                  onClick={() => handleGenerateQuiz(w)}
                  disabled={isGenerating}
                  className={cn(
                    "flex h-7 w-7 items-center justify-center rounded-xl border transition",
                    hasBank
                      ? "border-primary/30 bg-primary/8 text-primary-strong hover:bg-primary/15"
                      : "border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-primary",
                    isGenerating && "cursor-not-allowed opacity-60"
                  )}
                  title={hasBank ? "Regenerate quiz bank" : "Generate quiz bank"}
                >
                  {isGenerating
                    ? <Spinner size={13} weight="bold" className="animate-spin" />
                    : <Brain size={13} weight="bold" />
                  }
                </button>

                {/* View quiz — only shown if bank exists */}
                {hasBank && (
                  <button
                    onClick={() => setQuizModal(w)}
                    className="flex h-7 w-7 items-center justify-center rounded-xl border border-primary/30 bg-primary/8 text-primary-strong transition hover:bg-primary/15"
                    title="View / edit quiz bank"
                  >
                    <Eye size={13} weight="bold" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {data?.items.length === 0 && <p className="p-8 text-center text-sm font-semibold text-muted-foreground">No words match.</p>}
      </div>

      {data && data.pages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <BloomButton variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-prev"><CaretLeft size={16} weight="bold" /></BloomButton>
          <span className="text-sm font-extrabold text-muted-foreground">Page {data.page} of {data.pages}</span>
          <BloomButton variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="page-next"><CaretRight size={16} weight="bold" /></BloomButton>
        </div>
      )}

      {/* Word Preview panel */}
      <AnimatePresence>
        {preview && (
          <WordPreview
            word={preview}
            onClose={() => setPreview(null)}
            onEdit={() => { setEditor({ word: preview }); setPreview(null); }}
            onSaved={() => { load(); setPreview(null); }}
            onAddSimilar={(sourceWord) => { setPreview(null); setEditor({ similarTo: sourceWord }); }}
          />
        )}
      </AnimatePresence>

      {/* Word Editor */}
      {editor && (
        <WordEditor
          word={editor.word}
          similarTo={editor.similarTo}
          onClose={() => setEditor(null)}
          onSaved={load}
        />
      )}

      {/* Quiz Bank Modal */}
      {quizModal && (
        <QuizBankModal
          word={quizModal}
          onClose={() => setQuizModal(null)}
          onGenerated={() => {
            setBankIds((prev) => new Set([...prev, quizModal.word_id]));
          }}
        />
      )}
    </div>
  );
}
